import { Menu, Transition } from "@headlessui/react";
import {
  EllipsisVerticalIcon,
  UserPlusIcon,
  UserMinusIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import { NotificationContext } from "../../common/context/notification";
import { classNames } from "../../common/utils/classnames";
import {
  AccountQuery,
  useClaimAgentForAccountMutation,
} from "../../graphql/generated";
import { UnclaimAgentDialog } from "./unclaimAgentDialog";
import { ClaimAgentDialog } from "./claim-agent-dialog";
import { ZenDialogState } from "../../common/components/zen-dialog";

const AccountAgentActions: React.FC<{
  account?: AccountQuery["account"];
  refreshAccount?: () => void;
}> = ({ account, refreshAccount }) => {
  const [isClaimAgentDialogOpen, setClaimAgentDialogOpen] = React.useState(false);
  const [isUnclaimAgentDialogOpen, setUnclaimAgentDialogOpen] =
    React.useState(false);

  const { notifier } = React.useContext(NotificationContext);
  const [{ error, fetching }, mutation] = useClaimAgentForAccountMutation();

  React.useEffect(() => {
    if (error) {
      notifier.error(error);
    }
  }, [error]);

  const linkAgent = React.useCallback((selected?: string) => {
    if (!fetching && selected) {
      mutation({ accountId: account?.id || "", agentId: selected });
    }
  }, [account, fetching, mutation]);

  return (
    <>
      <Menu
        as="div"
        className="relative inline-block text-left mt-4 sm:mt-0 z-50"
      >
        <div>
          <Menu.Button className="bg-gray-100 rounded-full flex items-center text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-indigo-500">
            <span className="sr-only">Open options</span>
            <EllipsisVerticalIcon className="h-5 w-5" aria-hidden="true" />
          </Menu.Button>
        </div>

        <Transition
          as={React.Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-100 focus:outline-none">
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <div
                    className={classNames(
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                      "group flex items-center px-4 py-2 text-sm cursor-pointer"
                    )}
                    onClick={() => setClaimAgentDialogOpen(!isClaimAgentDialogOpen)}
                  >
                    <UserPlusIcon
                      className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                      aria-hidden="true"
                    />
                    Link new agent
                  </div>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <div
                    className={classNames(
                      active ? "bg-gray-100 text-gray-900" : "text-gray-700",
                      "group flex items-center px-4 py-2 text-sm cursor-pointer"
                    )}
                    onClick={() =>
                      setUnclaimAgentDialogOpen(!isUnclaimAgentDialogOpen)
                    }
                  >
                    <UserMinusIcon
                      className="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-500"
                      aria-hidden="true"
                    />
                    Unlink agent
                  </div>
                )}
              </Menu.Item>
            </div>
          </Menu.Items>
        </Transition>
      </Menu>

      <ClaimAgentDialog isOpen={isClaimAgentDialogOpen} onClose={() => {
        setClaimAgentDialogOpen(false);

      }} onSubmit={(id) => {
        linkAgent(id);
        setClaimAgentDialogOpen(false);
      }}
        state={fetching ? ZenDialogState.Submitting : ZenDialogState.Displaying}
      />

      <UnclaimAgentDialog
        isOpen={isUnclaimAgentDialogOpen}
        onClose={() => {
          setUnclaimAgentDialogOpen(false);
        }}
        onUpdate={() => {
          setUnclaimAgentDialogOpen(false);
          refreshAccount && refreshAccount();
        }}
        account={account}
      />
    </>
  );
};

export default AccountAgentActions;
